import { createTheme, ThemeOptions } from '@mui/material';
import React from 'react';

const fontSize = '1.6rem';
export const customTheme = {
    colors: {
        black: {
            0: '#FFFEFD',
            20: '#FBF9F7',
            50: '#f9f9f9',
            90: '#3C3C3C',
            100: '#f3f3f3',
            200: '#ebebeb',
            300: '#dbdbdb',
            400: '#b8b8b8',
            500: '#989898',
            550: '#7C7C7A',
            600: '#707070',
            700: '#5c5c5c',
            800: '#3d3d3d',
            900: '#1d1d1d',
            main: '#242625',
            disabled: '#D9D9D9',
        },
        primary: {
            50: '#fffee9',
            80: '#7C7C7A',
            bgLight: '#FFF6EF',
            bgMedium: '#FFF2E7',
            100: '#fffac7',
            200: '#fff6a0',
            300: '#fff17b',
            400: '#ffec5c',
            500: '#fee640',
            600: '#ffdd45',
            700: '#ffc941',
            800: '#ffb13a',
            900: '#FF882E',
            main: '#FF882E',
            bg: '#FFE8D6',
            bgDark: '#493021',
            950: '#ED7921',
            hover: '#ED7921',
        },
        secondary: {
            50: '#f8f5f4',
            100: '#eceae8',
            200: '#dedcda',
            300: '#c9c7c6',
            400: '#a4a1a0',
            500: '#82807e',
            600: '#5b5958',
            700: '#494745',
            800: '#2b2a28',
            hover: '#2b2a28',
            900: '#090604',
            main: '#090604',
        },
        success: {
            50: '#F4FAF3',
            100: '#E6F4E4',
            200: '#CDE8CA',
            300: '#A7D4A1',
            400: '#78B870',
            500: '#5FAD56',
            550: '#29CC6A',
            main: '#5FAD56',
            600: '#427F3A',
            hover: '#427F3A',
            700: '#356530',
            750: '#0D7656',
            800: '#2D512A',
            900: '#274324',
            950: '#10240F',
        },
        warn: {
            50: '#FFFAEB',
            100: '#FFF1C6',
            200: '#FFE288',
            300: '#FFCC49',
            main: '#FFCC49',
            400: '#FFB720',
            500: '#F99407',
            600: '#DD6D02',
            700: '#B74B06',
            800: '#94390C',
            900: '#7A300D',
            950: '#461702',
        },
        error: {
            bgLight: '#FEF8FA',
            50: '#FEF2F2',
            100: '#FFE1E1',
            200: '#FFC8C8',
            300: '#FFA2A2',
            400: '#FC5555',
            500: '#F53E3E',
            main: '#F53E3E',
            600: '#E22020',
            650: '#ED254E',
            655: '#ed254e1f',
            hover: '#E22020',
            700: '#BE1717',
            750: '#CC0037',
            800: '#9D1717',
            900: '#821A1A',
            950: '#470808',
        },
        shades: {
            600: '#5C46DB',
        },
        links: {
            light: '#DDF1FF',
            main: '#497CFF',
        },
        simpleshow: {
            main: '#00737a',
        },
        settings: '#F1F2F6',
        addCustom: '#1B1C1E',
        addCustomDisabled: '#94979c',
        white: '#FFFFFF',
        info: {
            main: '#FFFFFF',
            hover: '#f3f3f3',
        },
        grey: {
            400: '#9CA3AF',
            500: '#5C5C5C',
            700: '#374151',
            900: '#1C1D21',
            border: '#0000003a',
        },
    },
    fonts: { primary: 'Manrope', secondary: 'Manrope' },
    sizes: {
        subTitles: {
            fontSize,
        },
    },
};

const theme: ThemeOptions = {
    ...customTheme,
    palette: {
        primary: { ...customTheme.colors.primary },
        secondary: { ...customTheme.colors.secondary },
        error: { ...customTheme.colors.error },
        success: { ...customTheme.colors.success },
        action: {
            disabledBackground: '#CECAD1',
            disabled: 'white',
        },
    },
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    flexWrap: 'nowrap',
                },
                input: {
                    fontSize,
                },
                paper: {
                    fontSize,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    fontSize,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    height: '2.25em',
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
                disableTouchRipple: true,
            },
            styleOverrides: {
                root: {
                    '&:focus': {
                        outline: 'none',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                sizeMedium: {
                    fontFamily: 'Manrope',
                    fontSize: '1.6rem',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    // lineHeight: '1.5rem',
                },
                sizeLarge: {
                    fontFamily: 'Manrope',
                    fontSize: '1.8rem',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    // lineHeight: '1.75rem',
                },
                sizeSmall: {
                    fontFamily: 'Manrope',
                    fontSize: '0.1.5rem',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    // lineHeight: '1.25rem',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        borderRadius: '8px',
                    },
                },
            },
        },
    },
    typography: {
        h1: {
            fontFamily: 'Manrope',
            fontSize: '2.8rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            letterSpacing: '-0.035rem',
        },
        h2: {
            fontFamily: 'Manrope',
            fontSize: '2.4rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            letterSpacing: '-0.03rem',
        },
        h3: {
            fontFamily: 'Manrope',
            fontSize: '2rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            letterSpacing: '-0.025rem',
        },
        h4: {
            fontFamily: 'Manrope',
            fontSize: '1.6rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            letterSpacing: '-0.02rem',
        },
        h5: {
            fontFamily: 'Manrope',
            fontSize: '1.4rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            // letterSpacing: '-0.175rem',
        },
        h6: {
            fontFamily: 'Manrope',
            fontSize: '1.2rem',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            // letterSpacing: '-0.15rem',
        },
        body1: {
            fontFamily: 'Manrope',
            fontSize: '1.6rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.358rem',
            letterSpacing: '0.03rem',
        },
        body2: {
            fontFamily: 'Manrope',
            fontSize: '1.4rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.18825rem',
            letterSpacing: '0.02625rem',
        },
        body3: {
            fontFamily: 'Manrope',
            fontSize: '1.2rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.0185rem',
            letterSpacing: '0.0225rem',
        },
        buttonSm: {
            fontFamily: 'Manrope',
            fontSize: '1.4rem',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '1.25rem',
        },
        buttonMd: {
            fontFamily: 'Manrope',
            fontSize: '1.6rem',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '1.5rem',
        },
        buttonLg: {
            fontFamily: 'Manrope',
            fontSize: '1.8rem',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '1.75rem',
        },
    },
};

export default createTheme(theme);
type customThemeType = typeof customTheme;

declare module '@mui/material/styles' {
    interface Theme extends customThemeType {}
    // allow configuration using `createTheme`
    interface ThemeOptions extends customThemeType {}

    interface TypographyVariants {
        body3: React.CSSProperties;
        buttonSm: React.CSSProperties;
        buttonMd: React.CSSProperties;
        buttonLg: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        body3?: React.CSSProperties;
        buttonSm?: React.CSSProperties;
        buttonMd?: React.CSSProperties;
        buttonLg?: React.CSSProperties;
    }
}

declare module '@emotion/react' {
    interface Theme extends customThemeType, ThemeOptions {}
}
